import {
  NEARBY_RADIUS_M,
  NEARBY_RADIUS_WIDE_M,
  OSM_BACKOFF_MS,
  createThrottle,
  isOsmThrottleStatus,
  overpassNearbyQuery,
  overpassShopsQuery,
  parseNominatimReverse,
  parseNominatimSearch,
  parseOverpassResponse,
  placesCacheKey,
  type LatLng,
  type OsmPlaceCandidate,
  type ReverseGeocodeResult,
} from '@recipes/domain';
import { getPlatform, type Platform } from '@/lib/platform';

/**
 * Client-side venue lookup against OpenStreetMap (docs/finished/tasting-notes-spec.md 6.2, decision
 * 12.1): the backend is not involved, so usage limits apply per end user. Fair-use rules
 * are enforced here – one Overpass request per lookup, at most one Nominatim request per
 * second, results cached per geohash cell for 24 h, and a quiet back-off after throttling.
 */

export type PlacesLookupErrorReason = 'throttled' | 'unavailable' | 'disabled' | 'aborted';

export class PlacesLookupError extends Error {
  constructor(public readonly reason: PlacesLookupErrorReason) {
    super(`Places lookup failed: ${reason}`);
    this.name = 'PlacesLookupError';
  }
}

export interface PlacesClient {
  readonly enabled: boolean;
  /** `kind: 'shops'` looks for supermarkets, bakeries, markets … instead of food venues. */
  nearby(
    position: LatLng,
    options?: { signal?: AbortSignal; kind?: PlacesKind },
  ): Promise<OsmPlaceCandidate[]>;
  search(
    query: string,
    options?: { near?: LatLng | null; signal?: AbortSignal },
  ): Promise<OsmPlaceCandidate[]>;
  reverse(
    position: LatLng,
    options?: { signal?: AbortSignal },
  ): Promise<ReverseGeocodeResult | null>;
}

export type PlacesKind = 'food' | 'shops';

export interface PlacesCache {
  get(key: string): Promise<{ value: unknown; savedAt: number } | undefined>;
  set(key: string, value: unknown, savedAt: number): Promise<void>;
}

export interface OsmClientOptions {
  overpassUrl: string;
  nominatimUrl: string;
  platform?: () => Promise<Platform>;
  cache?: PlacesCache | null;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
  language?: () => string;
}

export const PLACES_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const NOMINATIM_INTERVAL_MS = 1000;

export function createMemoryCache(): PlacesCache {
  const map = new Map<string, { value: unknown; savedAt: number }>();
  return {
    get: async (key) => map.get(key),
    set: async (key, value, savedAt) => {
      map.set(key, { value, savedAt });
    },
  };
}

export function createOsmPlacesClient(options: OsmClientOptions): PlacesClient {
  const now = options.now ?? (() => Date.now());
  const platform = options.platform ?? getPlatform;
  const cache = options.cache === undefined ? createMemoryCache() : options.cache;
  const timeoutMs = options.timeoutMs ?? 8000;
  const throttleNominatim = createThrottle(NOMINATIM_INTERVAL_MS, now, options.sleep);
  const base = (url: string) => url.replace(/\/+$/, '');
  /** Per host: no requests before this time (set after 429/403/504). */
  const blockedUntil = new Map<'overpass' | 'nominatim', number>();

  async function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
    const hit = await cache?.get(key).catch(() => undefined);
    if (hit && now() - hit.savedAt < PLACES_CACHE_TTL_MS) return hit.value as T;
    const value = await load();
    await cache?.set(key, value, now()).catch(() => undefined);
    return value;
  }

  async function request(
    host: 'overpass' | 'nominatim',
    url: string,
    init: { method?: 'GET' | 'POST'; body?: string; headers?: Record<string, string> },
    signal?: AbortSignal,
  ): Promise<unknown> {
    if ((blockedUntil.get(host) ?? 0) > now()) throw new PlacesLookupError('throttled');
    // An already aborted signal fires no event.
    if (signal?.aborted) throw new PlacesLookupError('aborted');
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort);
    const timer = setTimeout(abort, timeoutMs);
    try {
      const res = await (await platform()).osmFetch(url, { ...init, signal: controller.signal });
      if (isOsmThrottleStatus(res.status)) {
        blockedUntil.set(host, now() + OSM_BACKOFF_MS);
        throw new PlacesLookupError('throttled');
      }
      if (res.status < 200 || res.status >= 300) throw new PlacesLookupError('unavailable');
      return await res.json();
    } catch (error) {
      if (error instanceof PlacesLookupError) throw error;
      if (signal?.aborted) throw new PlacesLookupError('aborted');
      // Timeouts and network errors: never retry in a loop, the caller falls back quietly.
      throw new PlacesLookupError('unavailable');
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }

  const overpass = (
    position: LatLng,
    radiusM: number,
    signal?: AbortSignal,
    kind: PlacesKind = 'food',
  ) =>
    cached(
      `${kind === 'shops' ? 'shops' : 'nearby'}:${placesCacheKey(position, radiusM)}`,
      async () =>
        parseOverpassResponse(
          await request(
            'overpass',
            base(options.overpassUrl),
            {
              method: 'POST',
              body: `data=${encodeURIComponent((kind === 'shops' ? overpassShopsQuery : overpassNearbyQuery)(position, radiusM, Math.ceil(timeoutMs / 1000)))}`,
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            },
            signal,
          ),
        ),
    );

  const nominatim = (path: string, params: Record<string, string>, signal?: AbortSignal) => {
    const query = new URLSearchParams({
      format: 'jsonv2',
      addressdetails: '1',
      'accept-language': options.language?.() ?? 'en',
      ...params,
    });
    return throttleNominatim(() =>
      request('nominatim', `${base(options.nominatimUrl)}${path}?${query.toString()}`, {}, signal),
    );
  };

  return {
    enabled: true,

    async nearby(position, { signal, kind } = {}) {
      const close = await overpass(position, NEARBY_RADIUS_M, signal, kind);
      if (close.length) return close;
      // Widen exactly once.
      return overpass(position, NEARBY_RADIUS_WIDE_M, signal, kind);
    },

    async search(text, { near, signal } = {}) {
      const q = text.trim();
      if (q.length < 2) return [];
      const params: Record<string, string> = { q, limit: '10', extratags: '1', dedupe: '1' };
      if (near) {
        // Bias (not restrict) towards the area around the last known position.
        const d = 0.25;
        params.viewbox = `${near.lng - d},${near.lat + d},${near.lng + d},${near.lat - d}`;
        params.bounded = '0';
      }
      const key = `search:${q.toLowerCase()}:${near ? placesCacheKey(near, 0) : '-'}`;
      return cached(key, async () =>
        parseNominatimSearch(await nominatim('/search', params, signal)),
      );
    },

    async reverse(position, { signal } = {}) {
      return cached(`reverse:${placesCacheKey(position, 0)}`, async () =>
        parseNominatimReverse(
          await nominatim(
            '/reverse',
            { lat: position.lat.toFixed(6), lon: position.lng.toFixed(6), zoom: '16' },
            signal,
          ),
        ),
      );
    },
  };
}

/** `VITE_PLACES_PROVIDER=none`: manual places only. */
export const disabledPlacesClient: PlacesClient = {
  enabled: false,
  nearby: async () => {
    throw new PlacesLookupError('disabled');
  },
  search: async () => {
    throw new PlacesLookupError('disabled');
  },
  reverse: async () => null,
};
